import React, { useEffect, useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Store, Warehouse } from "lucide-react";

const empty = () => ({ name: "", code: "", address: "", phone: "", manager_id: "", is_main: false, active: true });

export default function Branches() {
  const { user } = useAuth();
  const { reloadBranches } = useBranch();
  const isOwner = user.role === "super_admin";
  const [items, setItems] = useState([]);
  const [managers, setManagers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);

  const load = () => client.get("/branches", { params: { include_inactive: true } }).then((r) => setItems(r.data));
  useEffect(() => {
    load();
    if (isOwner) client.get("/users").then((r) => setManagers(r.data.filter((u) => u.role === "admin" && u.active !== false)));
  }, [isOwner]);

  const mgrName = useMemo(() => {
    const m = {}; managers.forEach((u) => { m[u.id] = u.name; }); return m;
  }, [managers]);

  const save = async () => {
    try {
      if (editId) await client.put(`/branches/${editId}`, form);
      else await client.post("/branches", form);
      toast.success("Cabang disimpan"); setOpen(false); load(); reloadBranches();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openEdit = (b) => {
    setForm({ name: b.name, code: b.code, address: b.address || "", phone: b.phone || "", manager_id: b.manager_id || "", is_main: !!b.is_main, active: b.active !== false });
    setEditId(b.id); setOpen(true);
  };

  return (
    <div className="p-6 space-y-5 animate-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Cabang</h1>
          <p className="text-sm text-muted-foreground">{isOwner ? "Kelola cabang & tugaskan ke Regional Manager" : "Kelola cabang region Anda"}</p>
        </div>
        <Button onClick={() => { setForm(empty()); setEditId(null); setOpen(true); }} data-testid="add-branch-btn"><Plus className="mr-2 h-4 w-4" /> Tambah</Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((b) => (
          <div key={b.id} className={`rounded-lg border bg-white p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md ${b.active === false ? "opacity-60" : ""}`} data-testid={`branch-card-${b.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                {b.is_main ? <Warehouse className="h-5 w-5 text-primary" /> : <Store className="h-5 w-5 text-primary" />}
              </div>
              <Button size="icon" variant="ghost" onClick={() => openEdit(b)} data-testid={`edit-branch-${b.id}`}><Pencil className="h-4 w-4" /></Button>
            </div>
            <div className="mt-3 flex items-center gap-2 font-heading text-lg font-semibold">{b.name}
              {b.is_main && <Badge className="bg-primary/10 text-primary">Gudang Utama</Badge>}
              {b.active === false && <Badge variant="secondary">Nonaktif</Badge>}
            </div>
            <div className="text-xs font-mono text-muted-foreground">{b.code}</div>
            <div className="mt-2 text-sm text-muted-foreground">{b.address}</div>
            <div className="text-sm text-muted-foreground">{b.phone}</div>
            {isOwner && <div className="mt-2 text-xs"><span className="text-muted-foreground">Regional Manager: </span><span className="font-medium">{mgrName[b.manager_id] || "—"}</span></div>}
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{editId ? "Ubah" : "Tambah"} Cabang</DialogTitle><DialogDescription>Isi data cabang toko lalu simpan.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="branch-name" /></div>
            <div className="space-y-2"><Label>Kode</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="branch-code" /></div>
            <div className="space-y-2"><Label>Alamat</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="space-y-2"><Label>Telepon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            {isOwner && (
              <div className="space-y-2"><Label>Regional Manager</Label>
                <Select value={form.manager_id} onValueChange={(v) => setForm({ ...form, manager_id: v })}>
                  <SelectTrigger data-testid="branch-manager"><SelectValue placeholder="Pilih Regional Manager" /></SelectTrigger>
                  <SelectContent>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center justify-between rounded-md border p-3">
              <div><div className="text-sm font-medium">Gudang Utama (pusat region)</div><div className="text-xs text-muted-foreground">Sumber transfer & PO</div></div>
              <Switch checked={form.is_main} onCheckedChange={(v) => setForm({ ...form, is_main: v })} data-testid="branch-is-main" />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div><div className="text-sm font-medium">Aktif</div><div className="text-xs text-muted-foreground">Nonaktifkan untuk sembunyikan cabang</div></div>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="branch-active" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="save-branch-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
